import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Menu, X, Search, ChevronDown, LogOut, LayoutDashboard, User } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

// Menu structure per VF3 spec: 8 top-level entries, 2 dropdowns.
const MENU = [
  { type: "link", to: "/", label: "Accueil" },
  { type: "link", to: "/je-metablis", label: "Je m'établis" },
  { type: "link", to: "/j-entreprends", label: "J'entreprends" },
  { type: "link", to: "/je-cherche-un-service", label: "Je cherche un service" },
  { type: "link", to: "/ici-quebec", label: "Ici Québec" },
  {
    type: "dropdown", label: "Annuaire", key: "annuaire",
    items: [
      { to: "/annuaire/professionnels", label: "Professionnels" },
      { to: "/annuaire/entrepreneurs", label: "Entrepreneurs" },
      { to: "/annuaire/organismes", label: "Organismes" },
    ],
  },
  {
    type: "dropdown", label: "Médiathèque", key: "mediatheque",
    items: [
      { to: "/blog", label: "Blog" },
      { to: "/agenda", label: "Agenda & Réalisations" },
      { to: "/guide-pratique", label: "Guide pratique" },
      { to: "/ressources-en-ligne", label: "Ressources en ligne" },
      { to: "/faq", label: "FAQ" },
    ],
  },
  { type: "link", to: "/a-propos", label: "À propos" },
];

function DesktopDropdown({ item, activePath }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const isActive = item.items.some((i) => activePath.startsWith(i.to));
  return (
    <div className="relative" ref={ref} data-testid={`nav-${item.key}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`px-3 py-2 rounded-full text-sm font-medium transition-colors inline-flex items-center gap-1 ${
          isActive ? "text-primary bg-secondary" : "text-foreground/70 hover:text-foreground hover:bg-secondary/60"
        }`}
      >
        {item.label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 mt-2 w-56 bg-white border border-emerald-900/10 rounded-2xl shadow-lg overflow-hidden z-50">
          {item.items.map((sub) => (
            <NavLink
              key={sub.to}
              to={sub.to}
              onClick={() => setOpen(false)}
              data-testid={`nav-sub-${sub.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => `block px-4 py-2.5 text-sm ${isActive ? "bg-secondary text-primary font-medium" : "text-foreground/80 hover:bg-secondary/60"}`}
            >
              {sub.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const dashPath = user?.role === "admin"
    ? "/admin"
    : ["professional", "entrepreneur", "organism"].includes(user?.role)
      ? "/dashboard/provider"
      : "/dashboard/client";

  return (
    <header className="glass-nav sticky top-0 z-50" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-18">
          <Link to="/" className="flex items-center gap-2 group flex-shrink-0" data-testid="logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_demka-connect/artifacts/th3maqcx_DemkaBIZ_transp-01.png"
              alt="DemkaBiz"
              className="h-10 sm:h-11 w-auto"
            />
          </Link>

          <nav className="hidden xl:flex items-center gap-1">
            {MENU.map((it) =>
              it.type === "dropdown" ? (
                <DesktopDropdown key={it.key} item={it} activePath={location.pathname} />
              ) : (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.to === "/"}
                  data-testid={`nav-${it.label.toLowerCase().replace(/[^\w]+/g, "-").replace(/^-|-$/g, "")}`}
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                      isActive ? "text-primary bg-secondary" : "text-foreground/70 hover:text-foreground hover:bg-secondary/60"
                    }`
                  }
                >
                  {it.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="hidden md:flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => navigate("/recherche")}
              className="p-2.5 rounded-full hover:bg-secondary text-foreground"
              data-testid="header-search-btn"
              aria-label="Recherche"
            >
              <Search className="w-5 h-5" />
            </button>
            {!user ? (
              <>
                <Link to="/connexion" className="hidden lg:inline-flex btn-outline text-sm px-4 py-2.5" data-testid="login-link">Se connecter</Link>
                <Link to="/inscription" className="btn-primary text-sm px-4 py-2.5" data-testid="register-link">Créer un compte</Link>
              </>
            ) : (
              <div className="relative">
                <button
                  onClick={() => setMenuOpen((v) => !v)}
                  className="btn-outline gap-2"
                  data-testid="user-menu-btn"
                >
                  <User className="w-4 h-4" />
                  <span className="truncate max-w-[120px]">{user.name}</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                {menuOpen && (
                  <div
                    className="absolute right-0 mt-2 w-56 bg-white border border-emerald-900/10 rounded-2xl shadow-lg overflow-hidden"
                    data-testid="user-menu"
                  >
                    <Link
                      to={dashPath}
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-3 text-sm hover:bg-secondary"
                      data-testid="dashboard-link"
                    >
                      <LayoutDashboard className="w-4 h-4" /> Tableau de bord
                    </Link>
                    <button
                      onClick={() => { setMenuOpen(false); logout(); navigate("/"); }}
                      className="w-full flex items-center gap-2 px-4 py-3 text-sm hover:bg-secondary text-left"
                      data-testid="logout-btn"
                    >
                      <LogOut className="w-4 h-4" /> Se déconnecter
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <button
            className="xl:hidden p-2 rounded-full hover:bg-secondary"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {open && (
          <div className="xl:hidden py-4 border-t border-emerald-900/10 space-y-1" data-testid="mobile-menu">
            {MENU.flatMap((it) => {
              if (it.type === "link") {
                return [(
                  <NavLink
                    key={it.to}
                    to={it.to}
                    end={it.to === "/"}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `block px-3 py-2 rounded-lg text-sm ${isActive ? "bg-secondary text-primary" : "text-foreground/80"}`
                    }
                  >
                    {it.label}
                  </NavLink>
                )];
              }
              return [
                <p key={`${it.key}-label`} className="px-3 pt-3 pb-1 text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">{it.label}</p>,
                ...it.items.map((sub) => (
                  <NavLink
                    key={sub.to}
                    to={sub.to}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `block px-6 py-2 rounded-lg text-sm ${isActive ? "bg-secondary text-primary" : "text-foreground/80"}`
                    }
                  >
                    {sub.label}
                  </NavLink>
                )),
              ];
            })}
            <div className="flex flex-col gap-2 pt-3">
              {!user ? (
                <>
                  <Link to="/connexion" onClick={() => setOpen(false)} className="btn-outline w-full">Se connecter</Link>
                  <Link to="/inscription" onClick={() => setOpen(false)} className="btn-primary w-full">Créer un compte</Link>
                </>
              ) : (
                <>
                  <Link to={dashPath} onClick={() => setOpen(false)} className="btn-outline w-full">
                    <LayoutDashboard className="w-4 h-4" /> Tableau de bord
                  </Link>
                  <button
                    onClick={() => { setOpen(false); logout(); navigate("/"); }}
                    className="btn-primary w-full"
                  >
                    <LogOut className="w-4 h-4" /> Déconnexion
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
